import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Table from "@/components/Table/table";
import { MotifButton, MotifProgressLoader } from "@ey-xd/motif-react";

import UploadEditDocument from "./uploadCreditorEditDoc/UploadEditCreditorDoc";
import { useCase } from "@/features/case/use-case";
import {
  useLazyDownloadDocumentQuery,
  useDeleteDocumentMutation,
} from "@/services/documents/documentsReducer";
import DeleteModal from "@/components/Modal/DeleteModal/DeleteModal";
import { useListDocumentsQuery } from "@/pages/CaseDetail/Creditor/CreditorSummaryView/CreditorSummaryReducer";
import { useColumnDefsCreditor } from "../../claimsTab/Constant/creditorHeaderConst";
import { useSelector } from "react-redux";
import {
  havingAddPermission,
  havingDeletePermission,
  havingEditPermission,
} from "@/utility/common";
import {
  CREATE_BUTTON_VARIANT,
  GetCommonDeleteImgButton,
  GetCommonDownloadImgButton,
} from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";

export const CreditorFileuploader = () => {
  const parameter = useParams();
  const { t } = useTranslation();
  const caseData = useCase();
  const { caseId } = caseData;
  const { data, isLoading, refetch } = useListDocumentsQuery({
    creditorId: parameter?.creditorId ?? "",
  });

  const permissions = useSelector((state: any) => state.permissions);
  const { Document } = permissions;

  const [processedData, setProcessedData] = useState<any>([]);
  useEffect(() => {
    let processedData = data?.map((doc: any) => {
      const updatedDoc = {
        ...doc,
        creditorId: parameter?.creditorId,
        caseId: caseId,
      };
      if (updatedDoc.entityType === 2) {
        return { ...updatedDoc, claimId: updatedDoc.entityId };
      }
      return updatedDoc;
    });
    setProcessedData(processedData ? processedData : []);
  }, [data, caseId, parameter?.creditorId]);

  const [showUploadEdit, setShowUploadEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDocument, setSelectedDocuement] = useState<any>({});
  const [downloadDocument, { isLoading: downloadLoading }] =
    useLazyDownloadDocumentQuery();
  const [deleteDocument, { isLoading: deleteLoading }] =
    useDeleteDocumentMutation();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (showUploadEdit === false) {
      refetch();
    }
    // eslint-disable-next-line
  }, [showUploadEdit]);

  function TitleComplement() {
    return (
      <>
        {havingAddPermission(Document) && (
          <MotifButton
            onClick={handleUploadNew}
            size="small"
            type="submit"
            variant={CREATE_BUTTON_VARIANT}
          >
            {t("global.globalActions.uploadNewDocument")}
          </MotifButton>
        )}
      </>
    );
  }

  const customActionHeader = {
    displayNameRenderer: function (props: any) {
      return (
        <div
          style={havingEditPermission(Document) ? { cursor: "pointer" } : {}}
          onClick={() => {
            if (havingEditPermission(Document)) {
              handleEdit(props.data);
            }
          }}
        >
          {props.data.displayName}
        </div>
      );
    },
    downloadActionRenderer: function (props: any) {
      return (
        <>
          <GetCommonDownloadImgButton
            loading={downloadLoading}
            onClick={() => handleDownload(props.data)}
          ></GetCommonDownloadImgButton>
        </>
      );
    },

    actionRenderer: function (props: any) {
      return (
        <>
          {havingDeletePermission(Document) && (
            <GetCommonDeleteImgButton
              onClick={() => {
                setSelectedDocuement(props.data);
                setShowDelete(true);
              }}
              disabled={props.data.claimReference}
            ></GetCommonDeleteImgButton>
          )}
        </>
      );
    },
  };

  const handleUploadNew = () => {
    setIsEdit(false);
    setSelectedDocuement({});
    setShowUploadEdit(true);
  };

  const handleEdit = (documentData: any) => {
    setIsEdit(true);
    setSelectedDocuement(documentData);
    setShowUploadEdit(true);
  };

  const handleDownload = (documentData: any) => {
    let fileExtensionName = documentData?.fileName?.split(".");
    let documentName = "";
    if (fileExtensionName.length > 1 && documentData?.displayName) {
      documentName = `${documentData.displayName}.${
        fileExtensionName[fileExtensionName?.length - 1]
      }`;
    } else {
      documentName = documentData.fileName;
    }

    downloadDocument({
      caseId: caseId,
      documentId: documentData.id,
      fileName: documentName,
    })
      .then((response) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response?.error?.data as string);
            setErrorMessage(errObj?.message || errObj.title);
          } catch (error) {
            setErrorMessage("Something went wrong!!");
            console.error("File download error:", error);
          }
        }
      })
      .catch((error: any) => {
        setErrorMessage("Something went wrong!!");
        console.error("File download error:", error);
      });
  };

  const handleDelete = () => {
    deleteDocument({
      CaseId: caseId,
      DocumentId: selectedDocument.id,
    }).then(() => {
      setShowDelete(false);
      refetch();
    });
  };
  const columnDefsCreditor = useColumnDefsCreditor(caseData?.abbreviation);

  return (
    <>
      <div className="mar-b-20">
        {isLoading ? (
          <MotifProgressLoader show variant="default" className="mar-b-20" />
        ) : (
          <>
            {errorMessage && (
              <div className="pad-b-10">
                <NotificationGrid
                  show={true}
                  type="error"
                  message={errorMessage}
                  scrollToTop={false}
                />
              </div>
            )}
            <Table
              columnDefs={columnDefsCreditor}
              cellRenderers={processedData}
              TitleComplement={TitleComplement}
              title={
                "pages.creditors.creditorDocumentUpload.creditorDocumentUploadTitle"
              }
              description={
                "pages.creditors.creditorDocumentUpload.creditorDocumentUploadDescription"
              }
              overlayNoRowsTemplate={t("global.globalNotifications.noRecords")}
              caseRenderers={customActionHeader}
              pagination={false}
            />
          </>
        )}
      </div>

      {showUploadEdit && (
        <UploadEditDocument
          show={showUploadEdit}
          setShow={setShowUploadEdit}
          creditorId={parameter?.creditorId!}
          isEdit={isEdit}
          document={selectedDocument}
        />
      )}

      <DeleteModal
        message={t("pages.claims.documentPopup.deleteMessage")}
        isVisible={showDelete}
        setIsVisible={setShowDelete}
        cancelBtnHandler={() => setShowDelete(false)}
        yesBtnHandler={handleDelete}
        deleteLoading={deleteLoading}
        entityName={selectedDocument?.displayName}
      />
    </>
  );
};
